import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useForm } from 'antd/es/form/Form';

import MasterService from '../../../../../../../../../shared/services/master';
import QuotationService from '../../../../../../../../../shared/services/quotation';

import { StepFourFormContext } from './StepFourFormContext';

import {
  CreditCardsInsured,
  SelectOption,
} from '../../../../../../../../../shared/interfaces';
import { UploadFile } from 'antd';

export interface ClientDataSectionForm {
  documentType: number;
  documentNumber: number;
  cuit: number;
  fullName: string;
  genderType: number;
  nationality: number;
  province: string;
  location: string;
  address: string;
  ivaCondition: number;
  perceptionAgent: string;
  personType: string;
  societyType: number;
  inscriptionDate: any;
  inscriptionNumber: string;
  contractDate: any;
}

export interface AdditionalDataSectionForm {
  telephoneNumber: string;
  telephoneNumberJob: string;
  cellphoneNumber: string;
  webPage: string;
  birthDate: string;
  placeOfBirth: string;
  countryBirth: number;
  profession: number;
  civilStatus: number;
  cuilNumber: number;
  economicActivity: number;
  addressEmail: string;
  rutaNumber: number;
}

export interface PaymentMethodSectionForm {
  paymentMethod: string;
  cbuNumber: number;
  cardNumber: number;
  companyCreditCard: number;
  expiration: string;
}

export const StepFourFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const [additionalDataSectionForm] = useForm<AdditionalDataSectionForm>();
  const [paymentMethodSectionForm] = useForm<PaymentMethodSectionForm>();
  const [tabsSectionForm] = useForm();
  const [validitySectionForm] = useForm<{ validityDate: any }>();

  const [additionalDataFormValues, setAdditionalDataFormValues] =
    useState<AdditionalDataSectionForm>();
  const [errorCreditCardMessage, setErrorCreditCardMessage] =
    useState<string>('');
  const [listCreditCardInsured, setListCreditCardInsured] =
    useState<CreditCardsInsured>();
  const [methodSelectedFromStepThree, setMethodSelectedFromStepThree] =
    useState<any>();
  const [paymentMethodFormValues, setPaymentMethodFormValues] =
    useState<PaymentMethodSectionForm>();
  const [isLoadingInspectionList, setIsLoadingInspectionList] =
    useState<boolean>(false);
  const [isLoadingUseList, setIsLoadingUseList] = useState<boolean>(false);
  const [isLoadingTrackersList, setIsLoadingTrackersList] =
    useState<boolean>(false);
  const [inspectionList, setInspectionList] = useState<Array<SelectOption>>([]);
  const [trackersList, setTrackersList] = useState<Array<SelectOption>>([]);
  const [collateralCreditors, setCollateralCreditors] = useState<
    Array<SelectOption>
  >([]);

  const [useList, setUseList] = useState<Array<SelectOption>>([]);

  const [fileSelect, setFileSelect] = useState<UploadFile[]>([]);


  const getTrackersList = useCallback(async () => {
    if (trackersList?.length) return;

    setIsLoadingTrackersList(true);

    try {
      const response = await QuotationService.getTrackersList();
      const data = response.data.rastreadoresw.map((tracker) => ({
        label: tracker.nombre,
        value: tracker.codigo,
      }));

      setTrackersList(data);
    } catch (error) {
    } finally {
      setIsLoadingTrackersList(false);
    }
  }, [setIsLoadingTrackersList, setTrackersList, trackersList]);

  const getUseList = useCallback(async () => {
    if (useList?.length) return;

    setIsLoadingUseList(true);

    try {
      const response = await MasterService.getUseList();

      setUseList(response.data);
    } catch (error) {
    } finally {
      setIsLoadingUseList(false);
    }
  }, [useList, setIsLoadingUseList, setUseList]);

  const getInspectionList = useCallback(async () => {
    if (inspectionList?.length) return;

    setIsLoadingInspectionList(true);

    try {
      const response = await QuotationService.getListCarInspectionTypes();

      setInspectionList(response);
    } catch (error) {
    } finally {
      setIsLoadingInspectionList(false);
    }
  }, [inspectionList, setIsLoadingInspectionList, setInspectionList]);

  const getCollateralCreditorsList = useCallback(async () => {
    try {
      const list = await QuotationService.getListOfCollateralCreditors();
      setCollateralCreditors(list);
    } catch (error) {}
  }, [setCollateralCreditors]);

  const getInitialInfo = useCallback(() => {
    Promise.all([
      getInspectionList(),
      getUseList(),
      getTrackersList(),
      getCollateralCreditorsList(),
    ]).catch(() => {});
  }, [
    getInspectionList,
    getUseList,
    getTrackersList,
    getCollateralCreditorsList,
  ]);

  useEffect(() => {
    getInitialInfo();
    /* eslint-disable-next-line */
  }, []);

  const values = useMemo(
    () => ({
      additionalDataFormValues,
      additionalDataSectionForm,
      errorCreditCardMessage,
      inspectionList,
      isLoadingInspectionList,
      isLoadingUseList,
      listCreditCardInsured,
      methodSelectedFromStepThree,
      paymentMethodFormValues,
      paymentMethodSectionForm,
      setAdditionalDataFormValues,
      setErrorCreditCardMessage,
      setListCreditCardInsured,
      setMethodSelectedFromStepThree,
      setPaymentMethodFormValues,
      tabsSectionForm,
      trackersList,
      useList,
      validitySectionForm,
      collateralCreditors,
      isLoadingTrackersList,
      fileSelect,
      setFileSelect
    }),
    [
      additionalDataFormValues,
      additionalDataSectionForm,
      errorCreditCardMessage,
      inspectionList,
      isLoadingInspectionList,
      isLoadingUseList,
      listCreditCardInsured,
      methodSelectedFromStepThree,
      paymentMethodFormValues,
      paymentMethodSectionForm,
      setAdditionalDataFormValues,
      setErrorCreditCardMessage,
      setListCreditCardInsured,
      setMethodSelectedFromStepThree,
      setPaymentMethodFormValues,
      tabsSectionForm,
      trackersList,
      useList,
      validitySectionForm,
      collateralCreditors,
      isLoadingTrackersList,
      fileSelect
    ]
  );

  return (
    <StepFourFormContext.Provider value={values}>
      {children}
    </StepFourFormContext.Provider>
  );
};
